import {Component, computed, inject, OnInit, signal} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {DatePipe} from '@angular/common';
import {CompanyService} from '@services/company.service';
import {ICompanyUserFile} from '@models/workspace.interface';
import {Download, LucideAngularModule, Trash2, Upload} from '@shared/components/icons/google-icons';
import {NotificationService} from '@services/frontend/notification.service';
import {ModalService} from '@services/frontend/modal.service';
import {DocumentDeleteDialog} from '@shared/components/document-delete-dialog/document-delete-dialog';
import {httpErrorMessage} from 'app/utils/http-error.utils';
import {formatFileSize} from 'app/utils/file-size.utils';

@Component({
  selector: 'app-company-documents',
  imports: [DatePipe, LucideAngularModule],
  templateUrl: './company-documents.html',
})
export class CompanyDocuments implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly companyService = inject(CompanyService);
  private readonly notificationService = inject(NotificationService);
  private readonly modalService = inject(ModalService);

  files = signal<ICompanyUserFile[]>([]);
  loading = signal(true);
  uploading = signal(false);
  downloadingId = signal<string | null>(null);
  deleting = signal(false);
  private siret = '';

  protected readonly Upload = Upload;
  protected readonly Download = Download;
  protected readonly Trash2 = Trash2;
  protected readonly formatSize = formatFileSize;

  busy = computed(() =>
    this.loading() || this.uploading() || this.downloadingId() !== null || this.deleting()
  );

  busyMessage = computed(() => {
    if (this.uploading()) {
      return 'Envoi du fichier en cours…';
    }
    if (this.downloadingId()) {
      return 'Téléchargement en cours…';
    }
    if (this.deleting()) {
      return 'Suppression en cours…';
    }
    return 'Chargement des documents…';
  });

  ngOnInit() {
    this.siret = this.route.parent!.snapshot.paramMap.get('siret')!;
    this.refresh();
  }

  refresh() {
    this.loading.set(true);
    this.companyService.listUserFiles(this.siret, 'GENERIC').subscribe({
      next: (f) => {
        this.files.set(f);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.notificationService.error('Impossible de charger les fichiers', 3000);
      },
    });
  }

  onFileSelected(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || this.busy()) {
      return;
    }
    this.uploading.set(true);
    this.companyService.uploadUserFile(this.siret, file, 'GENERIC').subscribe({
      next: () => {
        this.uploading.set(false);
        this.notificationService.success('Fichier envoyé', 2500);
        this.refresh();
      },
      error: (err) => {
        this.uploading.set(false);
        this.notificationService.error(httpErrorMessage(err, 'Échec de l’envoi du fichier'), 5000);
      },
    });
  }

  download(f: ICompanyUserFile) {
    if (this.busy()) {
      return;
    }
    this.downloadingId.set(f.id);
    this.companyService.downloadUserFile(this.siret, f.id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = f.originalFilename;
        a.click();
        window.URL.revokeObjectURL(url);
        this.downloadingId.set(null);
      },
      error: () => {
        this.downloadingId.set(null);
        this.notificationService.error('Téléchargement impossible', 3000);
      },
    });
  }

  delete(f: ICompanyUserFile) {
    if (this.busy()) {
      return;
    }
    const ref = this.modalService.open(DocumentDeleteDialog, {
      title: 'Confirmation',
      data: {fileName: f.originalFilename},
    });

    ref.afterClosed().subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }
      this.deleting.set(true);
      this.companyService.deleteUserFile(this.siret, f.id).subscribe({
        next: () => {
          this.deleting.set(false);
          this.notificationService.success('Document supprimé', 2500);
          this.files.update((current) => current.filter((item) => item.id !== f.id));
        },
        error: (err) => {
          this.deleting.set(false);
          this.notificationService.error(httpErrorMessage(err, 'Impossible de supprimer ce document'), 5000);
        },
      });
    });
  }
}
